import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {IReactionDisposer, reaction} from 'mobx';
import {observer, inject} from 'mobx-react';
import * as React from 'react';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(styles);

export enum MeshScrollDirection
{
    Vertical = 'Vertical',
    Horizontal = 'Horizontal'
}

interface IMeshScrollProps
{
    ctx?:IWSPMeshContext;
    direction:MeshScrollDirection;
}

@inject('ctx')
@observer
export class MeshScroll extends React.Component<IMeshScrollProps>
{
    private _elementRef:HTMLDivElement = null;
    private _scrollContentRef:HTMLDivElement = null;
    private _updateReactionDisposer:IReactionDisposer = null;

    @bind
    private _onElementRef(elementRef:HTMLDivElement):void
    {
        this._elementRef = elementRef;
    }

    @bind
    private _onScrollContentRef(elementRef:HTMLDivElement):void
    {
        this._scrollContentRef = elementRef;
    }

    @bind
    private _onScroll(event:React.UIEvent<HTMLDivElement>):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const {ctx, direction} = this.props;

        if( this._elementRef )
            requestAnimationFrame(() =>
            {
                mesh.setMeshScrollPosition(ctx,
                    direction === MeshScrollDirection.Horizontal
                        ? this._elementRef.scrollLeft
                        : null,
                    direction === MeshScrollDirection.Vertical
                        ? this._elementRef.scrollTop
                        : null
                );
            });
    }

    @bind
    private _getScrollPosition():number
    {
        const {ctx} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( !meshState )
            return 0;

        const {direction} = this.props;

        if( direction === MeshScrollDirection.Horizontal )
            return meshState.details.scrollLeft;
        else
            return meshState.details.scrollTop;
    }

    @bind
    private _setScrollPosition(scrollPosition:number):void
    {
        const {direction} = this.props;

        if( !this._scrollContentRef || scrollPosition == null )
            return;

        const style = this._contentStyle;

        this._scrollContentRef.style.height = style.height;
        this._scrollContentRef.style.width = style.width;

        if( direction === MeshScrollDirection.Horizontal )
        {
            if( this._scrollContentRef.scrollLeft !== scrollPosition )
                this._scrollContentRef.scrollLeft = scrollPosition;
        }
        else if( this._scrollContentRef.scrollTop !== scrollPosition )
            this._scrollContentRef.scrollTop = scrollPosition;
    }

    // @computed
    private get _contentStyle():React.CSSProperties
    {
        const {ctx, direction} = this.props;
        const meshState = controllers.ui.spreadsheets.mesh.getState(ctx);

        if( !meshState )
            return {};

        const {geometricSize: {width, height}} = meshState;

        return direction === MeshScrollDirection.Horizontal
            ? {
                width: `${width}px`
            } : {
                height: `${height}px`
            };
    }

    private _onMouseDown(event:React.MouseEvent<any>):void
    {
        event.stopPropagation();
    }

    public componentDidMount():void
    {
        this._updateReactionDisposer = reaction(this._getScrollPosition, this._setScrollPosition);
    }

    public componentWillUnmount():void
    {
        if( this._updateReactionDisposer )
        {
            this._updateReactionDisposer();
            this._updateReactionDisposer = null;
        }
    }

    public render():JSX.Element
    {
        const {direction} = this.props;

        return <div
            className={cx('meshScroll', direction)}
            onScroll={this._onScroll}
            onMouseDown={this._onMouseDown}
            ref={this._onElementRef}
        >
            <div className={styles.meshScrollContent} ref={this._onScrollContentRef} style={this._contentStyle} />
        </div>;
    }
}
